import { Component } from '@theme/component';

const PENDING_KEY = 'popup-request:pending';
const PENDING_TTL_MS = 30 * 60 * 1000; // ログイン往復の猶予。古い退避を後日勝手に送らないための TTL
const API_BASE = '/apps/mood-storefront/popup/requests';

/**
 * ポップアップ(Limited Store)アイテムリクエストボタン v2。
 *
 * - 未ログイン: サーバーレンダリングされたログインリンク(storefront_login_url)。
 *   クリック時に sessionStorage へ商品IDを退避し、サインイン後この商品ページへ戻ってきたら自動送信
 * - ログイン済み + クリック: 追加 API → 成功で「リクエスト済み」表示(+一覧リンク・取り消し)
 * - 「取り消す」: 削除 API → 未リクエスト表示に戻す
 */
class PopupRequestButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    if (this.#isLoggedIn && this.dataset.active !== 'true') {
      this.#maybeSubmitPending();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
  }

  get #productId() {
    return this.dataset.productId ?? '';
  }

  get #event() {
    return this.dataset.event ?? '';
  }

  get #isLoggedIn() {
    return this.dataset.loggedIn === 'true';
  }

  #onClick = (event) => {
    const loginLink = event.target.closest('[data-popup-request-login]');
    if (loginLink) {
      // 意思を退避してからログインへ(遷移はリンクに任せる)
      try {
        sessionStorage.setItem(
          PENDING_KEY,
          JSON.stringify({ productId: this.#productId, event: this.#event, at: Date.now() }),
        );
      } catch {
        /* sessionStorage 無効時は素通り。ログイン後に再クリックしてもらう */
      }
      return;
    }
    if (event.target.closest('a')) return;

    const remove = event.target.closest('.popup-request__remove');
    if (remove) {
      event.preventDefault();
      if (this.dataset.pending === 'true') return;
      void this.#submit('remove');
      return;
    }

    const button = event.target.closest('button.popup-request__button');
    if (!button) return;
    event.preventDefault();
    if (this.dataset.active === 'true' || this.dataset.pending === 'true') return;
    void this.#submit('add');
  };

  async #maybeSubmitPending() {
    let pending;
    try {
      const raw = sessionStorage.getItem(PENDING_KEY);
      if (!raw) return;
      pending = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem(PENDING_KEY);
      return;
    }
    if (!pending || pending.productId !== this.#productId || pending.event !== this.#event) return;
    // 重複発火防止: 消してから発火
    sessionStorage.removeItem(PENDING_KEY);
    if (typeof pending.at === 'number' && Date.now() - pending.at > PENDING_TTL_MS) return;
    await this.#submit('add');
  }

  async #submit(action) {
    this.dataset.pending = 'true';
    try {
      const res = await fetch(`${API_BASE}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: this.#productId, event: this.#event }),
      });
      if (res.status === 410) {
        this.#showToast('リクエストの受付は終了しました。');
        return;
      }
      if (!res.ok) throw new Error(`popup-request ${action} failed: ${res.status}`);
      this.#render(action === 'add');
      this.#showToast(
        action === 'add' ? this.dataset.toastSuccess || '' : this.dataset.toastRemoved || '',
      );
    } catch (error) {
      console.error('[popup-request] submit failed', error);
      this.#showToast(this.dataset.toastError || '');
    } finally {
      this.dataset.pending = 'false';
    }
  }

  /** Liquid の出力(snippet)と同じ構造を JS で組み直して状態を切り替える */
  #render(active) {
    this.dataset.active = active ? 'true' : 'false';
    const current = this.querySelector('.popup-request__button');
    const icon = current ? current.querySelector('.popup-request__icon') : null;
    const iconHtml = icon ? icon.outerHTML : '';
    const links = this.querySelector('.popup-request__links');
    if (links) links.remove();

    if (active) {
      const done = document.createElement('span');
      done.className = 'popup-request__button popup-request__button--done';
      done.innerHTML = `${iconHtml}<span class="popup-request__label"></span>`;
      done.querySelector('.popup-request__label').textContent = this.dataset.labelDone || '';
      if (current) current.replaceWith(done);
      else this.prepend(done);

      const wrap = document.createElement('span');
      wrap.className = 'popup-request__links';
      if (this.dataset.requestsUrl) {
        const list = document.createElement('a');
        list.className = 'popup-request__list-link';
        list.href = this.dataset.requestsUrl;
        list.textContent = 'リクエスト済みアイテムを見る';
        wrap.appendChild(list);
      }
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'popup-request__remove';
      remove.textContent = '取り消す';
      wrap.appendChild(remove);
      done.insertAdjacentElement('afterend', wrap);
    } else {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'popup-request__button';
      button.innerHTML = `${iconHtml}<span class="popup-request__label"></span>`;
      button.querySelector('.popup-request__label').textContent = this.dataset.labelIdle || '';
      if (current) current.replaceWith(button);
      else this.prepend(button);
    }
  }

  #showToast(message) {
    if (!message) return;
    const toast = document.createElement('div');
    toast.className = 'popup-request-toast';
    toast.setAttribute('role', 'status');
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('popup-request-toast--visible'));
    setTimeout(() => {
      toast.classList.remove('popup-request-toast--visible');
      setTimeout(() => toast.remove(), 300);
    }, 3200);
  }
}

if (!customElements.get('popup-request-button')) {
  customElements.define('popup-request-button', PopupRequestButton);
}
